import { Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import { ArrowRight, Compass, Home as HomeIcon, GraduationCap, Factory, HardHat, Building2 } from "lucide-react";
import { SectionHeader } from "./SectionHeader";
import { Reveal } from "./Reveal";

const AUDIENCES = [
  {
    Icon: Compass,
    title: "Architects & Interior Designers",
    text: "Technology planning support for luxury homes, auditoriums, commercial interiors, and intelligent buildings.",
    cta: "Collaborate on a Project",
  },
  {
    Icon: HomeIcon,
    title: "HNI Homeowners",
    text: "Smart home, private cinema, gate automation, security, lighting, and comfort systems planned before execution begins.",
    cta: "Plan a Luxury Residence",
  },
  {
    Icon: GraduationCap,
    title: "Institutions & Campuses",
    text: "Auditorium AV, stage lighting, acoustics coordination, classroom technology, and security infrastructure planning.",
    cta: "Discuss an Institutional Project",
  },
  {
    Icon: Factory,
    title: "Industrial & Logistics Sites",
    text: "Perimeter security, electric fencing, CCTV, analytics, access control, and command-centre planning for risk reduction.",
    cta: "Review Site Security",
  },
  {
    Icon: HardHat,
    title: "Consultants, PMCs & Contractors",
    text: "Technology design coordination that reduces rework, ambiguity, and integration failures during project execution.",
    cta: "Discuss Collaboration",
  },
  {
    Icon: Building2,
    title: "Builders & Developers",
    text: "Early-stage technology planning for premium residential, commercial, and mixed-use developments.",
    cta: "Plan Building Technology",
  },
];

export function WhoIHelp() {
  return (
    <section className="py-28 md:py-32">
      <div className="container-page">
        <SectionHeader
          eyebrow="Who I Help"
          index="002"
          title={<>Advisory built for the teams making <span className="text-primary">technology decisions</span>.</>}
          description="From architects shaping the brief to industrial owners protecting kilometres of perimeter — practical, vendor-neutral guidance at the stage it matters most."
        />
        <div className="mt-14 grid gap-px bg-border border border-border rounded-2xl overflow-hidden md:grid-cols-2 lg:grid-cols-3">
          {AUDIENCES.map((a, i) => (
            <Reveal key={a.title} delay={i * 0.04}>
              <motion.div
                whileHover={{ y: -2 }}
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                className="group h-full bg-background hover:bg-surface transition-colors p-7 flex flex-col"
              >
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <a.Icon className="h-4 w-4" strokeWidth={1.8} />
                </div>
                <h3 className="mt-5 text-lg font-semibold tracking-tight">{a.title}</h3>
                <p className="mt-3 text-sm text-ink-soft leading-relaxed flex-1">{a.text}</p>
                <Link
                  to="/contact"
                  className="tap-target mt-6 inline-flex items-center gap-1.5 py-2 text-xs font-semibold uppercase tracking-widest text-primary opacity-80 group-hover:opacity-100"
                >
                  {a.cta}
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
